import { getAllWorkflowActions } from './workflow-steps-config';

/**
 * Get the set of all valid workflow action values.
 * Cached after first call for performance.
 */
let validActionValuesCache = null;

export const getValidActionValues = () => {
  if (!validActionValuesCache) {
    validActionValuesCache = new Set(getAllWorkflowActions().map((action) => action.value));
  }
  return validActionValuesCache;
};

/**
 * Check if a single action value is a known, valid workflow action.
 * @param {string} actionValue - The action value to validate
 * @returns {boolean}
 */
export const isValidActionValue = (actionValue) => {
  if (typeof actionValue !== 'string') return false;
  return getValidActionValues().has(actionValue);
};

/**
 * Validate an entire workflow object for import.
 * Returns an object with { valid: boolean, errors: string[] }.
 *
 * @param {Object} workflowData - The parsed workflow JSON
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateWorkflowForImport = (workflowData) => {
  const errors = [];

  // Validate name
  if (!workflowData.name || typeof workflowData.name !== 'string') {
    errors.push('Workflow must have a valid name');
  }

  if (workflowData.name && workflowData.name.length < 3) {
    errors.push('Workflow name must be at least 3 characters');
  }

  // Validate steps array
  if (!Array.isArray(workflowData.steps)) {
    errors.push('Workflow must have a steps array');
    return { valid: false, errors };
  }

  if (workflowData.steps.length < 2) {
    errors.push('Workflow must have at least 2 steps');
  }

  // Validate each step's action value
  const validValues = getValidActionValues();

  workflowData.steps.forEach((step, index) => {
    const actionValue = typeof step.action === 'string' ? step.action : step.action?.value;

    if (!actionValue) {
      errors.push(`Step ${index + 1}: Missing action value`);
      return;
    }

    if (typeof actionValue !== 'string') {
      errors.push(`Step ${index + 1}: Action value must be a string`);
      return;
    }

    if (!validValues.has(actionValue)) {
      errors.push(`Step ${index + 1}: Unknown action "${actionValue}". Only registered workflow actions are allowed.`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Escape HTML entities in a string to prevent XSS.
 * Use as a safety net for any user-controlled text that might
 * end up in an {@html} context.
 *
 * @param {string} str
 * @returns {string}
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};
